'use client';

import { useEffect, useRef, useState } from 'react';


interface HeroStat {
  label: string;
  value: number;
  suffix: string;
  icon: React.ReactNode;
  color: string;
}

function AnimatedCounter({ target, duration = 1800 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    const animate = (timestamp: number) => {
      if (!startRef.current) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  return <>{count.toLocaleString()}</>;
}

export default function HeroSection() {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const [discordStats, setDiscordStats] = useState<{ memberCount: number; presenceCount: number } | null>(null);
  const [isLoadingDiscord, setIsLoadingDiscord] = useState(true);

  // Fetch real-time Discord stats
  useEffect(() => {
    const fetchDiscordStats = async () => {
      try {
        const response = await fetch('/api/discord/guild-info');
        if (response.ok) {
          const data = await response.json();
          setDiscordStats({
            memberCount: data.memberCount || 0,
            presenceCount: data.presenceCount || 0,
          });
        }
      } catch (error) {
        console.error('Failed to fetch Discord stats:', error);
      } finally {
        setIsLoadingDiscord(false);
      }
    };

    fetchDiscordStats();
  }, []);

  // Live stats with real Discord data
  const stats = [
    {
      label: 'Players Online',
      value: discordStats?.presenceCount || 0,
      suffix: '',
      color: '#22c55e',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
        </svg>
      ),
    },
    {
      label: 'Discord Members',
      value: discordStats?.memberCount || 0,
      suffix: '+',
      color: '#5865f2',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
        </svg>
      ),
    },
    {
      label: 'Total Players',
      value: 5000,
      suffix: '+',
      color: '#6a5cff',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center px-6 pt-20"
      aria-label="VANTA Hero"
    >
      {/* Background accent glow blobs - repositioned */}
      <div
        aria-hidden="true"
        className="absolute top-1/4 right-1/3 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(106,92,255,0.12) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(34,197,94,0.06) 0%, transparent 70%)',
          filter: 'blur(70px)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto w-full">
        {/* Asymmetric Layout - Content centered, stats inline below */}
        <div className="flex flex-col items-center text-center">
          {/* Main Content */}
          <div className="max-w-4xl">
            {/* Live badge */}
            <div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass border border-white/10 mb-10 animate-slide-in-left"
              style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0s' }}
            >
              <span className="live-dot" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-widest uppercase text-vanta-muted">
                EU Servers Live
              </span>
              <span className="stat-badge">Vanilla</span>
            </div>

            {/* Main headline */}
            <h1
              className="font-heading mb-8 leading-none animate-scale-in"
              style={{
                fontSize: 'clamp(4.5rem, 12vw, 10rem)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                opacity: 0,
                animationFillMode: 'forwards',
                animationDelay: '0.15s',
              }}
            >
              <span className="gradient-text">VANTA</span>
            </h1>

            {/* Tagline */}
            <p
              className="font-heading text-3xl md:text-4xl font-700 text-white mb-5 animate-slide-in-right"
              style={{ opacity: 0, animationFillMode: 'forwards', fontWeight: 700, animationDelay: '0.3s' }}
            >
              Pure Vanilla.{' '}
              <span className="text-vanta-accent-light">Zero BS.</span>
            </p>

            {/* Subtitle */}
            <p
              className="text-lg md:text-xl text-vanta-muted font-normal max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in"
              style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0.45s' }}
            >
              EU premium vanilla Rust servers. No pay-to-win, no wipe anxiety.
              Just raw, fair Rust the way it was meant to be played.
            </p>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-slide-in-up"
              style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0.6s' }}
            >
              <a
                href="steam://connect/play.vanta.gg"
                className="btn-primary px-10 py-5 text-lg flex items-center justify-center gap-3 group shadow-accent"
                aria-label="Join VANTA Rust Server"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.497 1.009 2.455-.397.957-1.497 1.41-2.455 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.252 0-2.265-1.014-2.265-2.265z"/>
                </svg>
                Join Server
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
              <a
                href="https://discord.gg/gJgHMDZAES"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary px-10 py-5 text-lg flex items-center justify-center gap-3"
                aria-label="Join VANTA Discord"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                </svg>
                Join Discord
              </a>
            </div>
          </div>

          {/* Stats Cards - Horizontal inline layout */}
          <div
            ref={statsRef}
            className="flex flex-wrap justify-center gap-6 w-full max-w-5xl animate-slide-in-up"
            style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0.75s' }}
          >
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className="hero-stat-card-inline px-8 py-6 flex items-center gap-5 min-w-[240px]"
                style={{ animationDelay: `${0.75 + i * 0.1}s` }}
              >
                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
                  style={{ background: `${stat.color}22`, color: stat.color, boxShadow: `0 0 20px ${stat.color}30` }}
                  aria-hidden="true"
                >
                  {stat.icon}
                </div>
                {/* Value */}
                <div>
                  <div
                    className="font-heading text-3xl font-900 text-white leading-none mb-1.5"
                    style={{ fontWeight: 900, color: stat.color }}
                    aria-label={`${stat.label}: ${stat.value}${stat.suffix}`}
                  >
                    {statsVisible ? (
                      <>
                        <AnimatedCounter target={stat.value} />
                        {stat.suffix}
                      </>
                    ) : (
                      '—'
                    )}
                  </div>
                  <div className="text-xs text-vanta-muted font-semibold uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
                {/* Live dot for player count */}
                {i === 0 && (
                  <div className="ml-auto flex-shrink-0">
                    <span className="live-dot" aria-label="Live" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}